/**
 * Axios 统一实例 + 拦截器
 *
 * 安全升级: Token 通过 httpOnly Cookie 管理，不再存 localStorage
 *   - 请求拦截器: Cookie 自动发送（withCredentials），附加 X-Teacher-Id
 *   - 响应拦截器: 403 → Toast 提示, 401 → Cookie 刷新 / 清除登录状态
 *
 * Cookie 由后端 set_cookie 设置:
 *   - access_token_cookie: httpOnly, Secure, SameSite=Lax
 *   - refresh_token_cookie: httpOnly, Secure, SameSite=Lax
 */
import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'
import { BUILD_ID } from '../buildInfo'

const API_BASE = '/api'

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // ← 自动发送 httpOnly Cookie
})

// Refresh must not pass through apiClient's response interceptor. Otherwise a
// 401 from /auth/refresh waits on its own in-flight promise forever.
const refreshClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

/**
 * Teacher Web authentication has a single source of truth: the httpOnly
 * access_token cookie. Older builds wrote Bearer tokens into Axios defaults;
 * a tab surviving a deployment could otherwise keep sending that stale token
 * ahead of the newly issued login cookie.
 */
function clearLegacyAuthHeaders(headers: any) {
  if (!headers) return
  for (const name of ['Authorization', 'authorization', 'X-Auth-Token', 'x-auth-token']) {
    if (typeof headers.delete === 'function') headers.delete(name)
    delete headers[name]
  }
}

clearLegacyAuthHeaders(apiClient.defaults.headers.common)

// ─── Token 刷新锁 ────────────────────────────────
// Several page-level requests can receive the same expired-cookie 401. Keep
// one refresh in flight and make every failed request wait for its outcome.
// A boolean lock made the second request log the teacher out while the first
// request was still successfully refreshing the httpOnly cookie.
let refreshPromise: Promise<void> | null = null
let logoutStarted = false

// ─── 请求拦截器 ──────────────────────────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // This Axios instance is exclusively for teacher Web APIs. Terminal/Agent
  // JWTs use separate clients and must never leak into this request path.
  clearLegacyAuthHeaders(apiClient.defaults.headers.common)
  clearLegacyAuthHeaders(config.headers)
  // Browser must generate the multipart boundary for FormData. The global
  // JSON default is otherwise allowed to leak into file/text imports.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type')
    }
    delete (config.headers as any)['Content-Type']
    delete (config.headers as any)['content-type']
  }
  // httpOnly Cookie 自动发送（withCredentials），无需手动设置 Authorization
  // 附加 X-Teacher-Id 用于日志和降级场景
  config.headers['Cache-Control'] = 'no-store'
  config.headers['Pragma'] = 'no-cache'
  config.headers['X-Frontend-Build'] = BUILD_ID
  const tid = localStorage.getItem('teacher_id')
  if (tid) {
    config.headers['X-Teacher-Id'] = tid
  }
  return config
})

// ─── 响应拦截器 ──────────────────────────────────
const LOGIN_KEYS = [
  'logged_in', 'teacher_id', 'teacher_name',
  'teacher_school', 'teacher_username', 'teacher_role',
  'teacher_subject', 'is_admin',
]

apiClient.interceptors.response.use(
  (response: any) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // ── 429 Too Many Requests: 限流 ──────────
    if (error.response?.status === 429) {
      const detail = (error.response.data as any)?.detail || '请求过于频繁，请稍后再试'
      console.warn(`[API 429] ${detail}`, originalRequest?.url)
      window.dispatchEvent(new CustomEvent('api:rate-limited', {
        detail: { message: detail, url: originalRequest?.url }
      }))
      return Promise.reject(error)
    }

    // ── 409 Conflict: 并发冲突 ──────────────────
    if (error.response?.status === 409) {
      const msg = getErrorMessage(error, '数据已被他人修改')
      console.warn(`[API 409] ${msg}`, originalRequest?.url)
      window.dispatchEvent(new CustomEvent('api:conflict', {
        detail: { message: msg, url: originalRequest?.url }
      }))
      return Promise.reject(error)
    }

    // ── 403 Forbidden: 无权限 ──────────────────
    if (error.response?.status === 403) {
      const msg = getErrorMessage(error, '无操作权限')
      console.warn(`[API 403] ${msg}`, originalRequest?.url)
      if (!originalRequest?.suppressGlobalErrorNotification) {
        window.dispatchEvent(new CustomEvent('api:forbidden', {
          detail: { message: msg, url: originalRequest?.url }
        }))
      }
      return Promise.reject(error)
    }

    // ── 401 Unauthorized ─────────────────────
    if (error.response?.status === 401 && !originalRequest._retry) {
      const detail = (error.response.data as any)?.detail || ''
      // S4: 扩展可刷新的错误场景 (类型错误、Token无效也可能是cookie问题)
      const canRefresh = detail.includes('过期') || detail.includes('expired')
        || detail.includes('Token') || detail.includes('类型')
      if (canRefresh) {
        originalRequest._retry = true
        try {
          if (!refreshPromise) {
            refreshPromise = refreshClient.post('/auth/refresh', {}).then((resp) => {
              if (!(resp.data?.success || resp.status === 200)) {
                throw new Error('会话刷新失败')
              }
            }).finally(() => {
              refreshPromise = null
            })
          }
          await refreshPromise
          return apiClient(originalRequest)
        } catch {
          // The common logout path below is deliberately shared by all
          // waiters. It runs only when the refresh request itself failed.
        }
      }
      // Only an explicit refresh failure, or a non-refreshable 401, may end
      // the session. A second in-flight request must never race this cleanup.
      if (!logoutStarted) {
        logoutStarted = true
        LOGIN_KEYS.forEach(k => { try { localStorage.removeItem(k) } catch { /* ignore */ } })
        delete apiClient.defaults.headers.common['Authorization']
        try { sessionStorage.removeItem('rbac_scope') } catch { /* ignore */ }
        window.location.replace('/v2/login')
      }
      return Promise.reject(error)
    }
    return Promise.reject(error)
  }
)

export default apiClient

/** RFC 7807 错误响应解析 — 优先 title（用户友好），detail 作为补充 */
export function getErrorMessage(err: any, fallback = '操作失败'): string {
  const data = err?.response?.data
  if (!data) return err?.message || fallback
  const title = (data as any)?.title || ''
  const detail = (data as any)?.detail || ''
  if (title && detail) return `${title}：${detail}`
  if (detail) return detail
  if (title) return title
  return err?.message || fallback
}

// ── v45.0: Response Normalization ─────────────────
// 后端两种响应格式并存: {data:[],total} vs {plural_key:[],total}
// Store 层统一归一化, 页面只消费归一化后的格式

export interface NormalizedList<T> {
  items: T[]
  total: number
}

export function normalizeResponse<T>(
  resData: any,
  knownKeys: string[],          // 可能的列表键名 (按优先级)
  fallbackKey: string = 'data',
): NormalizedList<T> {
  const d = resData?.data ?? resData
  if (!d) return { items: [], total: 0 }
  for (const key of knownKeys) {
    if (Array.isArray(d[key])) {
      return { items: d[key] as T[], total: d.total ?? d.count ?? d[key].length }
    }
  }
  if (Array.isArray(d)) return { items: d as T[], total: d.length }
  if (Array.isArray(d[fallbackKey])) {
    return { items: d[fallbackKey] as T[], total: d.total ?? d.count ?? d[fallbackKey].length }
  }
  return { items: [], total: 0 }
}

/** @deprecated Teacher Web authentication is cookie-only. */
export function setAuthToken(_token: string | null) {
  clearLegacyAuthHeaders(apiClient.defaults.headers.common)
}

/** 清除所有登录状态 */
export function clearAuthState() {
  LOGIN_KEYS.forEach(k => localStorage.removeItem(k))
  clearLegacyAuthHeaders(apiClient.defaults.headers.common)
  window.location.href = '/'
}

